import {
	LaytimeTerms,
	PortActionTypes,
} from '@shared/utils/constants';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { CargoProps } from '@api/models/cargo';
import type { Port } from '@api/utils/ports';

type PortRotationEntry = Pick<
	GetEstimateDetailsResponse['portRotationEntries'][number],
	'type' | 'cargoId' | 'portCost' | 'calculationMethod' | 'loadDischargeRate' | 'timeAllowed' | 'timeAllowedOverride'
> & {
	port: Pick<Port, 'name' | 'eu'> | null;
};

type FixDataEstimate = Pick<GetEstimateDetailsResponse, 'vesselId' | 'bunkerExpenses'> & {
	portRotationEntries: PortRotationEntry[];
	result: Pick<GetEstimateDetailsResponse['result'], 'totalEUAcost'> | null;
};

type FixDataCargo = Pick<
	CargoProps,
	'id' | 'type' | 'laytimeTerms' | 'timeAllowed' | 'timeAllowedLoading' | 'timeAllowedDischarging'
>;

const NO_VESSEL_ID = -1;

const isMissing = (value: number | string | null | undefined) => value == null || Number(value) === 0;

const isCargoPortEntry = (entry: PortRotationEntry) => (
	entry.type === PortActionTypes.LOADING || entry.type === PortActionTypes.DISCHARGING
);

const formatPorts = (entries: PortRotationEntry[]) => (
	[...new Set(entries.map((entry) => entry.port?.name ?? 'Unknown port'))].join(', ')
);

const hasCargoLevelTimeAllowed = (cargo: FixDataCargo) => (
	cargo.laytimeTerms === LaytimeTerms.REVERSIBLE || cargo.laytimeTerms === LaytimeTerms.AVERAGED
);

const hasCargoTimeAllowed = (cargo: FixDataCargo) => {
	if (cargo.laytimeTerms === LaytimeTerms.REVERSIBLE) {
		return !isMissing(cargo.timeAllowed);
	}

	return !isMissing(cargo.timeAllowedLoading) && !isMissing(cargo.timeAllowedDischarging);
};

export const getMissingFixData = ({
	estimate,
	cargos,
}: {
	estimate: FixDataEstimate;
	cargos: FixDataCargo[];
}) => {
	const missing: string[] = [];
	const entries = estimate.portRotationEntries;
	const cargoPortEntries = entries.filter(isCargoPortEntry);

	if (estimate.vesselId == null || estimate.vesselId === NO_VESSEL_ID) {
		missing.push('Vessel');
	}

	const unpricedGrades = Object.entries(estimate.bunkerExpenses ?? {})
		.filter(([, expense]) => !isMissing(expense?.consumption) && isMissing(expense?.pricePerTon))
		.map(([fuelGrade]) => fuelGrade);

	if (unpricedGrades.length > 0) {
		missing.push(`Bunker price: ${unpricedGrades.join(', ')}`);
	}

	const entriesWithoutPortCosts = entries.filter((entry) => (
		(isCargoPortEntry(entry) || entry.type === PortActionTypes.CANAL_TRANSIT) &&
		isMissing(entry.portCost)
	));

	if (entriesWithoutPortCosts.length > 0) {
		missing.push(`Port costs: ${formatPorts(entriesWithoutPortCosts)}`);
	}

	const entriesWithoutCalculationMethod = cargoPortEntries.filter((entry) => entry.calculationMethod == null);

	if (entriesWithoutCalculationMethod.length > 0) {
		missing.push(`Laytime calculation method: ${formatPorts(entriesWithoutCalculationMethod)}`);
	}

	const portLevelCargoIds = cargos
		.filter((cargo) => !hasCargoLevelTimeAllowed(cargo))
		.map((cargo) => cargo.id);

	const entriesWithoutTimeAllowed = cargoPortEntries.filter((entry) => (
		entry.cargoId != null &&
		portLevelCargoIds.includes(entry.cargoId) &&
		isMissing(entry.loadDischargeRate) &&
		isMissing(entry.timeAllowed) &&
		isMissing(entry.timeAllowedOverride)
	));

	if (entriesWithoutTimeAllowed.length > 0) {
		missing.push(`L/D rate or time allowed: ${formatPorts(entriesWithoutTimeAllowed)}`);
	}

	const cargosWithoutTimeAllowed = cargos.filter((cargo) => (
		hasCargoLevelTimeAllowed(cargo) && !hasCargoTimeAllowed(cargo)
	));

	if (cargosWithoutTimeAllowed.length > 0) {
		missing.push(`Time allowed: ${cargosWithoutTimeAllowed.map((cargo) => cargo.type ?? 'Cargo').join(', ')}`);
	}

	if (entries.some((entry) => entry.port?.eu === true) && isMissing(estimate.result?.totalEUAcost)) {
		missing.push('EU ETS cost');
	}

	return missing;
};
